import { useMemo } from "react";

import type {
  IAnalyticsResponse,
  Period,
} from "../../hooks/reactQueryHooks/useAnalytic";
import {
  formatCurrency,
  formatCurrencyNoCent,
  formatNumber,
  shortNumber,
} from "../../utils/NumbersFormatting";
import type { ILabelColors, ILabelValue, ISeries } from "./types";
import { ChartUtils } from "./utils";

export interface IStackedChartProps {
  bottomValue: number[];
  topValue: number[];
}

export interface IBarChartProps {
  leftValue: number[];
  rightValue: number[];
}

export const costsAndSavingsLabelFormatter = (value: string) =>
  formatCurrencyNoCent(Number(value));
export const tokensLabelFormatter = (value: string) =>
  shortNumber(Number(value));
export const cacheHitsLabelFormatter = (value: string) =>
  formatNumber(Number(value));
export const cacheHitRatioLabelFormatter = (value: string) =>
  Math.round(Number(value)) + "%";
export const requestsLabelFormatter = (value: string) =>
  shortNumber(Number(value));
export const latencyLabelFormatter = (value: string) =>
  formatNumber(Number(value));

const getAverageValue = (values: number[]): number => {
  let notZeroLatencyCount = 0;
  const sum = values.reduce((acc, item) => {
    if (item !== 0) {
      notZeroLatencyCount++;
    }
    return acc + item;
  }, 0);
  return notZeroLatencyCount > 0 ? Math.round(sum / notZeroLatencyCount) : 0;
};

export const useCharts = ({
  analyticData,
  period = "day",
  index = 0,
}: {
  analyticData?: IAnalyticsResponse;
  period?: Period;
  index?: number;
}) => {
  const formattedData = useMemo(() => {
    const requests: IStackedChartProps = {
      topValue: [],
      bottomValue: [],
    };
    const latency: number[] = [];
    const xAxisData: string[] = [];
    const tooltipTitles: string[] = [];
    const tokens: IStackedChartProps = {
      topValue: [],
      bottomValue: [],
    };
    const cacheHitRatios: number[][] = [[], []];
    const cacheHits: IStackedChartProps = {
      topValue: [],
      bottomValue: [],
    };
    const costsAndSavings: IBarChartProps = {
      leftValue: [],
      rightValue: [],
    };
    const cacheHitsTopValues: number[] = [];
    const cacheHitsBottomValues: number[] = [];
    const requestsTopValues: number[] = [];
    const requestsBottomValues: number[] = [];
    const tokensTopValues: number[] = [];
    const tokensBottomValues: number[] = [];
    const costsAndSavingsLeftValues: number[] = [];
    const costsAndSavingsRightValues: number[] = [];
    let xAxisBuilt = false;

    if (analyticData?.cacheHitRatios) {
      analyticData?.cacheHitRatios.forEach(item => {
        cacheHitRatios[0].push(item.instantCacheHitRatio * 100);
        cacheHitRatios[1].push(item.cumulativeCacheHitRatio * 100);
      });
    }

    if (analyticData?.latency) {
      let withData = false;
      analyticData?.latency.forEach(item => {
        const xAxisLabel = ChartUtils.getXAxisLabel(period, item.date);
        if (period === "hour") {
          const formatter = new Intl.DateTimeFormat("en-US", {
            hour: "numeric",
            minute: "numeric",
          });
          tooltipTitles.push(
            formatter.format(new Date(item.date)) +
              " - " +
              formatter.format(
                new Date(new Date(item.date).getTime() + 1000 * 60 * 60),
              ),
          );
        }
        xAxisData.push(xAxisLabel);

        latency.push(Math.round(item.averageLatency));
        xAxisBuilt = true;
        if (item.averageLatency) {
          withData = true;
        }
      });
      if (!withData) {
        latency.length = 0;
      }
    }

    if (analyticData?.requests) {
      let withData = false;
      analyticData?.requests.forEach(request => {
        let topRequestValue = 0;
        let bottomRequestValue = 0;
        let cacheHitsTopValue = 0;
        let cacheHitsBottomValue = 0;
        if (request.items.length) {
          withData = true;
        }
        request.items.forEach(cacheType => {
          if (cacheType.httpStatusCode !== 200) {
            topRequestValue += cacheType.count;
          } else {
            bottomRequestValue += cacheType.count;
          }
          if (cacheType.cacheType === "original") {
            cacheHitsBottomValue += cacheType.count;
          }
          if (cacheType.cacheType === "similar") {
            cacheHitsTopValue += cacheType.count;
          }
        });
        cacheHitsTopValues.push(cacheHitsTopValue);
        cacheHitsBottomValues.push(cacheHitsBottomValue);
        requestsTopValues.push(topRequestValue);
        requestsBottomValues.push(bottomRequestValue);
      });
      cacheHits.topValue = withData ? cacheHitsTopValues : [];
      cacheHits.bottomValue = withData ? cacheHitsBottomValues : [];
      requests.topValue = withData ? requestsTopValues : [];
      requests.bottomValue = withData ? requestsBottomValues : [];
    }

    if (analyticData?.tokens) {
      let withData = false;
      analyticData?.tokens.forEach(item => {
        if (!xAxisBuilt) {
          const xAxisLabel = ChartUtils.getXAxisLabel(period, item.date);
          xAxisData.push(xAxisLabel);
        }
        let topValue = 0;
        let bottomValue = 0;
        item.items.forEach(tokenType => {
          if (tokenType.cacheType === "none") {
            bottomValue += tokenType.count;
          } else {
            topValue += tokenType.count;
          }
        });
        if (topValue || bottomValue) {
          withData = true;
        }
        tokensTopValues.push(topValue);
        tokensBottomValues.push(bottomValue);
      });
      tokens.topValue = withData ? tokensTopValues : [];
      tokens.bottomValue = withData ? tokensBottomValues : [];
    }

    if (analyticData?.prices) {
      let withData = false;
      analyticData?.prices.forEach(item => {
        if (item.costs !== 0 || item.savings !== 0) {
          withData = true;
        }
        costsAndSavingsLeftValues.push(item.costs);
        costsAndSavingsRightValues.push(item.savings);
      });
      costsAndSavings.leftValue = withData ? costsAndSavingsLeftValues : [];
      costsAndSavings.rightValue = withData ? costsAndSavingsRightValues : [];
    }
    return {
      requests,
      latency,
      xAxisData,
      tokens,
      cacheHitRatios,
      cacheHits,
      costsAndSavings,
      tooltipTitles,
    };
  }, [analyticData, period]);

  const badgesData = useMemo(() => {
    const result: {
      value: string;
      rightItems?: string[];
    }[] = [];
    if (formattedData.costsAndSavings) {
      let totalCosts = 0;
      let costsSaving = 0;
      formattedData.costsAndSavings?.leftValue.forEach(price => {
        totalCosts += price;
      });
      formattedData.costsAndSavings?.rightValue.forEach(saving => {
        costsSaving += saving;
        totalCosts += saving;
      });
      result.push({
        value: formatCurrency(totalCosts),
        rightItems: [formatCurrency(costsSaving)],
      });
    }

    if (formattedData.tokens) {
      let totalTokens = 0;
      let paidTokens = 0;
      let freeTokens = 0;
      formattedData.tokens?.topValue.forEach(tokens => {
        totalTokens += tokens;
        freeTokens += tokens;
      });
      formattedData.tokens?.bottomValue.forEach(tokens => {
        totalTokens += tokens;
        paidTokens += tokens;
      });
      result.push({
        value: shortNumber(totalTokens),
        rightItems: [shortNumber(paidTokens), shortNumber(freeTokens)],
      });
    }

    if (formattedData.cacheHits) {
      result.push({
        value: getAverageValue(formattedData.cacheHitRatios[1]) + "%",
      });
    }

    return result;
  }, [formattedData]);

  const costAndSavingsChartData = useMemo(() => {
    const costAndSavings = {
      costs: formattedData.costsAndSavings.leftValue,
      savings: formattedData.costsAndSavings.rightValue,
    };

    let labelValues: ILabelValue[] = [
      {
        value: "$0",
        color: "#3C19CD" as ILabelColors,
        label: "Costs",
      },
      {
        value: "$0",
        color: "#0FAF74" as ILabelColors,
        label: "Savings",
      },
    ];
    let series: ISeries[] = [];

    if (!!costAndSavings.costs.length || !!costAndSavings.savings.length) {
      labelValues = [
        {
          value: formatCurrencyNoCent(
            costAndSavings.costs.reduce((acc, item) => acc + item, 0),
          ),
          color: "#3C19CD" as ILabelColors,
          label: "Costs",
        },
        {
          value: formatCurrencyNoCent(
            costAndSavings.savings.reduce((acc, item) => acc + item, 0),
          ),
          color: "#0FAF74" as ILabelColors,
          label: "Savings",
        },
      ];
      series = [
        {
          data: costAndSavings.costs,
          color: "#3C19CD",
          name: "Costs",
          altColor: "#8DA6FF",
        },
        {
          data: costAndSavings.savings,
          color: "#0FAF74",
          name: "Savings",
        },
      ];
    }

    return {
      tooltipTitles: formattedData.tooltipTitles,
      valueUnit: "$",
      xAxisData: formattedData.xAxisData,
      yLabelFormatter: costsAndSavingsLabelFormatter,
      label: {
        label: "Costs and savings",
        values: labelValues,
      },
      series,
    };
  }, [formattedData]);

  const cacheHitsChartData = useMemo(() => {
    const chartData = {
      semanticHits: formattedData.cacheHits.topValue,
      exactHits: formattedData.cacheHits.bottomValue,
    };
    let labelValues: ILabelValue[] = [
      {
        value: "0",
        color: "#0FAF74" as ILabelColors,
        label: "Exact hit",
      },
      {
        value: "0",
        color: "#3C19CD" as ILabelColors,
        label: "Semantic hit",
      },
    ];
    let series: ISeries[] = [];
    if (!!chartData.exactHits.length || !!chartData.semanticHits.length) {
      labelValues = [
        {
          value: formatNumber(
            chartData.exactHits.reduce((acc, item) => acc + item, 0),
          ),
          color: "#0FAF74" as ILabelColors,
          label: "Exact hit",
        },
        {
          value: formatNumber(
            chartData.semanticHits.reduce((acc, item) => acc + item, 0),
          ),
          color: "#3C19CD" as ILabelColors,
          label: "Semantic hit",
        },
      ];
      series = [
        {
          data: chartData.exactHits,
          color: "#0FAF74",
          name: "Exact hit",
        },
        {
          data: chartData.semanticHits,
          color: "#3C19CD",
          name: "Semantic hit",
          altColor: "#8DA6FF",
        },
      ];
    }
    return {
      tooltipTitles: formattedData.tooltipTitles,
      yLabelFormatter: cacheHitsLabelFormatter,
      tooltipValueFormatter: formatNumber,
      xAxisData: formattedData.xAxisData,
      valueUnit: "",
      label: {
        label: "Cache hits",
        values: labelValues,
      },
      series,
    };
  }, [formattedData]);

  const cacheHitRatioChartData = useMemo(() => {
    const chartData = formattedData.cacheHitRatios[index];
    let labelValues: ILabelValue[] = [
      {
        value: "0%",
      },
    ];
    let series: ISeries[] = [];
    if (chartData.length) {
      labelValues = [{ value: getAverageValue(chartData) + "%" }];

      series = [
        {
          data: chartData,
          name: "Cache hit ratio",
        },
      ];
    }
    return {
      tooltipTitles: [],
      valueUnit: "",
      xAxisData: formattedData.xAxisData,
      yLabelFormatter: cacheHitRatioLabelFormatter,
      label: {
        label: "Cache hit ratio",
        values: labelValues,
      },
      series,
    };
  }, [formattedData, index]);

  const requestsChartData = useMemo(() => {
    const chartData = {
      requests: formattedData.requests.bottomValue,
      errors: formattedData.requests.topValue,
    };
    let labelValues = "0";
    let series: any[] = [];

    if (chartData.requests.length || chartData.errors.length) {
      const sum =
        chartData.requests.reduce((acc, item) => acc + item, 0) +
        chartData.errors.reduce((acc, item) => acc + item, 0);
      labelValues = shortNumber(sum);
      series = [
        {
          data: chartData.requests,
          color: "#0FAF74",
          name: "Success",
        },
        {
          data: chartData.errors,
          color: "#FF3838",
          name: "Errors",
        },
      ];
    }
    return {
      sumLabel: "Total requests",
      tooltipTitles: formattedData.tooltipTitles,
      valueUnit: "",
      yLabelFormatter: requestsLabelFormatter,
      xAxisData: formattedData.xAxisData,
      label: {
        label: "Requests",
        values: [
          {
            value: labelValues,
          },
        ],
      },
      series: series,
    };
  }, [formattedData]);

  const latencyChartData = useMemo(() => {
    const chartData = formattedData.latency;
    let labelValues: ILabelValue[] = [
      {
        value: 0 + "ms / req",
      },
    ];
    let series: ISeries[] = [];
    if (chartData.length) {
      labelValues = [
        {
          value: formatNumber(getAverageValue(chartData)) + "ms / req",
        },
      ];
      series = [
        {
          data: chartData,
          color: "#3C19CD",
          name: "Latency",
        },
      ];
    }
    return {
      tooltipTitles: formattedData.tooltipTitles,
      valueUnit: "ms / req",
      xAxisData: formattedData.xAxisData,
      yLabelFormatter: latencyLabelFormatter,
      label: {
        label: "Latency",
        values: labelValues,
      },
      series,
    };
  }, [formattedData]);

  const tokensUsageChartData = useMemo(() => {
    const chartData = {
      paid: formattedData.tokens.bottomValue,
      free: formattedData.tokens.topValue,
    };
    let labelValues: ILabelValue[] = [
      {
        value: "0",
        color: "#A347F8" as ILabelColors,
        label: "Paid tokens",
      },
      {
        value: "0",
        color: "#0FAF74" as ILabelColors,
        label: "Free tokens",
      },
    ];
    let series: ISeries[] = [];
    if (chartData.paid.length || chartData.free.length) {
      labelValues = [
        {
          value: String(
            shortNumber(
              formattedData.tokens.bottomValue.reduce(
                (acc, item) => acc + item,
                0,
              ),
            ),
          ),
          color: "#A347F8" as ILabelColors,
          label: "Paid tokens",
        },
        {
          value: String(
            shortNumber(
              formattedData.tokens.topValue.reduce(
                (acc, item) => acc + item,
                0,
              ),
            ),
          ),
          color: "#0FAF74" as ILabelColors,
          label: "Free tokens",
        },
      ];
      series = [
        {
          data: chartData.paid,
          color: "#A347F8",
          name: "Paid tokens",
        },
        {
          data: chartData.free,
          color: "#0FAF74",
          name: "Free tokens",
        },
      ];
    }

    return {
      tooltipTitles: formattedData.tooltipTitles,
      valueUnit: "",
      xAxisData: formattedData.xAxisData,
      reorderTooltipItems: true,
      yLabelFormatter: tokensLabelFormatter,
      label: {
        label: "Tokens usage",
        values: labelValues,
      },
      series,
    };
  }, [formattedData]);

  return {
    cacheHitsChartData,
    cacheHitRatioChartData,
    requestsChartData,
    latencyChartData,
    tokensUsageChartData,
    costAndSavingsChartData,
    badgesData,
  };
};
